import React, { useState, useEffect, useCallback } from "react";
import DataTable from "react-data-table-component";
import ProductDetailModal from "../../components/ProductDetailModal";
import SupplierEditModal from "../../components/SupplierEditModal";
import ImportSupplierInventoryModal from "../../components/ImportSupplierInventoryModal";
import LoadingModal from "../../components/LoadingModal";
import Modal from "react-modal";
import { adminServices } from "../../services/AdminServices";
import { toast } from "react-toastify";
import axios from "axios";
import { ArrowLeft, Edit, User, Phone, Mail, CreditCard, MapPin, Truck, Package, Star, Download, Upload, X, ChevronLeft, ChevronRight } from "lucide-react";

const paginationStyles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "16px 0",
    gap: "8px",
    flexWrap: "wrap"
  },
  link: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "6px 8px"
  },
  linkDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.7
  },
  pageNum: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    minWidth: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "6px"
  },
  pageNumActive: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
    fontWeight: 500
  },
  ellipsis: {
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "0 4px",
    minWidth: "24px",
    textAlign: "center"
  }
};

function CustomPagination({ rowCount, rowsPerPage, currentPage, onChangePage, totalRows }) {
  const total = rowCount ?? totalRows ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
      if (totalPages > 5) pages.push("...");
      if (totalPages > 5) pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      if (totalPages > 5) pages.push("...");
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div style={paginationStyles.wrapper}>
      <button
        type="button"
        aria-label="Previous page"
        style={{
          ...paginationStyles.link,
          ...(!canPrev ? paginationStyles.linkDisabled : {})
        }}
        onClick={() => canPrev && onChangePage(currentPage - 1)}
        disabled={!canPrev}
      >
        <ChevronLeft size={18} />
        Previous
      </button>
      {pageNumbers.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} style={paginationStyles.ellipsis}>
            ...
          </span>
        ) : (
          <button
            key={p}
            type="button"
            style={{
              ...paginationStyles.pageNum,
              ...(p === currentPage ? paginationStyles.pageNumActive : {})
            }}
            onClick={() => onChangePage(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Next page"
        style={{
          ...paginationStyles.link,
          ...(!canNext ? paginationStyles.linkDisabled : {})
        }}
        onClick={() => canNext && onChangePage(currentPage + 1)}
        disabled={!canNext}
      >
        Next
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

const SupplierDetail = ({ supplier, onBack }) => {
  const formatSupplierCode = (val) => {
    if (val == null || val === "") return "-";
    const s = String(val);
    if (/^\d+$/.test(s)) return `SUP-${s.padStart(3, "0")}`;
    return s;
  };

  const normalizeSupplier = useCallback((raw) => {
    const data = raw?.originalData ?? raw ?? {};
    const statusRaw = data.status;
    const normalizedStatus =
      statusRaw === 1 || statusRaw === "Active" || statusRaw === "active" ? "Active"
      : statusRaw === 0 || statusRaw === "Inactive" || statusRaw === "inactive" ? "Inactive"
      : typeof statusRaw === "string" ? statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase()
      : "-";

    const addressParts = [
      data.address,
      data.city,
      data.region
    ].filter((x) => x != null && String(x).trim() !== "");

    const normalizeToArray = (val) => {
      if (val == null) return [];
      if (Array.isArray(val)) return val.filter(Boolean).map(String);
      if (typeof val === "string" && val.trim() !== "") return [val.trim()];
      return [];
    };

    return {
      id: data.id ?? raw?.id,
      supplierId: data.id ?? raw?.supplierId ?? raw?.id,
      supplierCode: formatSupplierCode(data.id ?? raw?.supplierId ?? raw?.id),
      supplierName: data.supplierName ?? raw?.supplierName ?? raw?.name ?? "-",
      status: normalizedStatus,
      contactPerson: data.contactName ?? raw?.contactPerson ?? raw?.contactName ?? "-",
      phone: data.contactNo ?? raw?.phone ?? raw?.contactNo ?? "-",
      email: data.email ?? raw?.email ?? "-",
      paymentMethod: data.paymentMethod ?? data.payment_method ?? raw?.paymentMethod ?? "-",
      shipmentPeriod:
        data.shipmentPeriod ??
        data.shipment_period ??
        raw?.shipmentPeriod ??
        raw?.shipment_period ??
        "-",
      // Keep legacy UI blocks even if API doesn't send these yet.
      categories: normalizeToArray(data.categories ?? data.category ?? raw?.categories ?? raw?.category),
      supplierType: data.type?.name ?? raw?.type?.name ?? raw?.businessCategory ?? "",
      supplierTypeOptions: normalizeToArray(raw?.supplierTypeOptions),
      fulfillmentRate: raw?.fulfillmentRate ?? data.fulfillmentRate ?? "-",
      standardShipmentPeriod:
        // explicit standard shipment period if provided
        raw?.standardShipmentPeriod ??
        data.standardShipmentPeriod ??
        // fallback to main shipment period if standard is missing
        data.shipmentPeriod ??
        data.shipment_period ??
        raw?.shipmentPeriod ??
        raw?.shipment_period ??
        "-",
      address: addressParts.length > 0 ? addressParts.join(", ") : (data.address ?? raw?.address ?? "-"),
      supplierInventories: data.supplierInventories ?? raw?.supplierInventories ?? raw?.originalData?.supplierInventories ?? [],
      originalData: data
    };
  }, []);

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const isActive = status === "Active";
    return (
      <span 
        className="supplier-detail-status-badge"
        style={{
          backgroundColor: isActive ? "rgba(53, 187, 0, 0.1)" : "#e5e7eb",
          color: isActive ? "#35BB00" : "#000000"
        }}
      >
        {status}
      </span>
    );
  };

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [productDetailLoading, setProductDetailLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [isAddInventoryModalOpen, setIsAddInventoryModalOpen] = useState(false);
  const [addInventoryLoading, setAddInventoryLoading] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableProductsLoading, setAvailableProductsLoading] = useState(false);
  const [uomList, setUomList] = useState([]);
  const [uomLoading, setUomLoading] = useState(false);
  const [selectedProductUoms, setSelectedProductUoms] = useState([]);

  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [productDropdownSearch, setProductDropdownSearch] = useState("");
  const [addInventoryForm, setAddInventoryForm] = useState({
    productId: "",
    uomId: "",
    unitPrice: "",
    minOrderQuantity: 1,
    leadTime: "",
    status: "ACTIVE",
  });

  // Default supplier data if none provided
  const [supplierData, setSupplierData] = useState(supplier ? normalizeSupplier(supplier) : null);

  // Update supplierData when supplier prop changes
  useEffect(() => {
    if (supplier) {
      setSupplierData(normalizeSupplier(supplier));
    }
  }, [supplier, normalizeSupplier]);

  // Derive products from supplierInventories in supplier data (from GET /Suppliers/{id})
  const mapSupplierInventoriesToProducts = useCallback((inventories) => {
    if (!inventories || !Array.isArray(inventories)) return [];
    return inventories.map((item, index) => {
      const product = item.product || item;
      const sku = product.sku || product.productSku || item.sku || "-";
      const name = product.description || product.productName || product.name || item.description || item.productName || "-";
      const price = product.marketPrice ?? product.unitPrice ?? item.unitPrice ?? item.price ?? 0;
      const priceStr = price !== undefined && price !== null ? `₱${Number(price).toLocaleString()}` : "-";
      const uomObj = item.uom || product.uom;
      const uom = (typeof uomObj === "object" && uomObj?.name) ? uomObj.name : (product.smallestUOM || product.uom || item.uom || item.unitOfMeasure || "-");
      const moq = item.minOrderQuantity ?? item.moq ?? product.moq ?? item.minimumOrderQuantity ?? 0;
      const leadTime = item.leadTime ?? product.leadTime ?? "-";
      return {
        id: item.inventoryId ?? item.id ?? product.id ?? index + 1,
        sku,
        itemName: name,
        unitPrice: priceStr,
        pricePerLoad: priceStr,
        uom,
        moq,
        leadTime: typeof leadTime === "number" ? `${leadTime} days` : (leadTime || "-"),
        image: product.image || product.imageUrl || null,
        priceHistory: item.supplierInventoryPriceHistories || item.priceHistory || product.priceHistory || [],
        originalData: item
      };
    });
  }, []);

  useEffect(() => {
    const inventories =
      supplierData?.originalData?.supplierInventories ??
      supplierData?.supplierInventories ??
      [];
    setProducts(mapSupplierInventoriesToProducts(inventories));
  }, [supplierData?.originalData?.supplierInventories, supplierData?.supplierInventories, mapSupplierInventoriesToProducts]);

  const normalizeUom = (u) => {
    if (!u) return null;
    const id = u.id ?? u.uomId ?? u.uom_id ?? u.UomId ?? u.ID ?? u.Id;
    const name = u.name ?? u.uomName ?? u.uom_name ?? u.UomName ?? u.Name;
    if (id == null || String(name || "").trim() === "") return null;
    return { id, name: String(name).trim() };
  };

  // Recompute selected product UOMs if the UOM list arrives after a product was chosen
  useEffect(() => {
    const productId = addInventoryForm.productId;
    if (!productId || uomList.length === 0 || selectedProductUoms.length > 0) return;
    const p = availableProducts.find((prod) => String(prod.id) === String(productId));
    if (!p) return;

    let productUoms = [];
    if (Array.isArray(p.productUoms) && p.productUoms.length > 0) {
      productUoms = p.productUoms.map(normalizeUom).filter(Boolean);
    }
    if (productUoms.length === 0 && p.assignedUomIds && p.assignedUomIds.length > 0) {
      productUoms = p.assignedUomIds
        .map((id) => uomList.find((u) => String(u.id) === String(id)))
        .filter(Boolean);
    }
    if (productUoms.length === 0) {
      const names = Array.isArray(p.assignedUomNames) ? p.assignedUomNames : [];
      productUoms = names
        .map((name) =>
          uomList.find(
            (u) => String(u.name).toLowerCase() === String(name).toLowerCase()
          )
        )
        .filter(Boolean);
    }
    const seen = new Set();
    productUoms = productUoms.filter((u) => {
      if (!u || u.id == null || seen.has(String(u.id))) return false;
      seen.add(String(u.id));
      return true;
    });

    if (productUoms.length > 0) {
      setSelectedProductUoms(productUoms);
      setAddInventoryForm((prev) => ({
        ...prev,
        uomId: prev.uomId || productUoms[0]?.id || ""
      }));
    }
  }, [uomList, addInventoryForm.productId, availableProducts, selectedProductUoms]);

  // Re-fetch supplier from /Suppliers/{id} to refresh products (e.g. after import)
  const refreshSupplierData = useCallback(async () => {
    const supplierId = supplierData?.originalData?.id ?? supplierData?.id ?? supplierData?.supplierId;
    if (!supplierId) return;
    setProductsLoading(true);
    try {
      const response = await adminServices.getData(`Suppliers/${supplierId}`);
      if (response && response.data) {
        const data = response.data;
        setSupplierData({
          ...supplierData,
          ...normalizeSupplier({ originalData: data }),
        });
        setProducts(mapSupplierInventoriesToProducts(data.supplierInventories));
      }
    } catch (error) {
      console.error("Error refreshing supplier data:", error);
    } finally {
      setProductsLoading(false);
    }
  }, [supplierData, mapSupplierInventoriesToProducts, normalizeSupplier]);

  // Download template for supplier inventory
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL || ''}SupplierInventory/download-template`,
        {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('appToken'),
          },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'supplier-inventory-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error(error.response?.data?.message || "Failed to download template. Please try again.");
    }
  };

  const exportToCSV = (data) => {
    if (!data || data.length === 0) {
      toast.warning("No data to export");
      return;
    }
    const headers = ["SKU", "Item Name", "Unit Price", "Price per Load", "UOM", "MOQ", "Lead Time"];
    const rows = data.map((item) => [
      item.sku || "",
      item.itemName || "",
      item.unitPrice || "",
      item.pricePerLoad || "",
      item.uom || "",
      item.moq ?? "",
      item.leadTime || ""
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `supplier-inventory-${supplierData?.supplierId || supplierData?.id || "export"}.csv`);
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Export completed");
  };

  const ensureAvailableProductsLoaded = async () => {
    if (availableProductsLoading || availableProducts.length > 0) return;
    setAvailableProductsLoading(true);
    try {
      const res = await adminServices.getData("products/data");
      const raw = Array.isArray(res?.data) ? res.data : [];
      const mapped = raw.map((p, idx) => {
        const assignedUomIds = [
          p.uomId,
          p.uom_id,
          p.largestUOMId,
          p.largestUomId,
          p.largest_uom_id,
          p.smallestUOMId,
          p.smallestUomId,
          p.smallest_uom_id,
        ].filter(Boolean);
        const assignedUomNames = [
          p.uomName,
          p.uom_name,
          p.uom?.name,
          p.largestUOM,
          p.largest_uom,
          p.smallestUOM,
          p.smallest_uom,
          p.unitOfMeasure,
          p.unit_of_measure,
        ].filter(Boolean);
        const productUoms = Array.isArray(p.uoms)
          ? p.uoms
          : Array.isArray(p.productUoms)
          ? p.productUoms
          : Array.isArray(p.product_uoms)
          ? p.product_uoms
          : [];
        return {
          id: p.id ?? p.productId ?? idx + 1,
          sku: p.sku ?? p.productSku ?? "-",
          name: p.description ?? p.productName ?? p.name ?? "-",
          marketPrice: p.marketPrice ?? p.price ?? 0,
          uomId: p.uomId ?? p.uom_id ?? p.largestUOMId ?? p.largestUomId ?? p.largest_uom_id ?? "",
          uomName: p.uomName ?? p.uom_name ?? p.uom?.name ?? "",
          assignedUomIds,
          assignedUomNames,
          productUoms,
        };
      });
      setAvailableProducts(mapped);
    } catch (error) {
      console.error("Error fetching products for supplier inventory:", error);
      toast.error("Failed to load products list.");
      setAvailableProducts([]);
    } finally {
      setAvailableProductsLoading(false);
    }
  };

  const fetchUomList = async () => {
    if (uomLoading || uomList.length > 0) return;
    setUomLoading(true);
    try {
      const res = await adminServices.getUnitOfMeasurements();
      const data = res?.data?.data ?? res?.data ?? [];
      const normalized = (Array.isArray(data) ? data : [])
        .map(normalizeUom)
        .filter(Boolean);
      setUomList(normalized);
    } catch (error) {
      console.error("Error fetching UOM list:", error);
      setUomList([]);
    } finally {
      setUomLoading(false);
    }
  };

  const openAddInventoryModal = async () => {
    await ensureAvailableProductsLoaded();
    await fetchUomList();
    setAddInventoryForm({
      productId: "",
      uomId: "",
      unitPrice: "",
      minOrderQuantity: 1,
      leadTime: "",
      status: "ACTIVE",
    });
    setSelectedProductUoms([]);
    setIsAddInventoryModalOpen(true);
  };

  const handleAddSupplierInventory = async () => {
    const supplierId = supplierData?.originalData?.id ?? supplierData?.id ?? supplierData?.supplierId;
    if (!supplierId) {
      toast.error("Supplier not found. Cannot add inventory.");
      return;
    }
    if (!addInventoryForm.productId) {
      toast.error("Please select a product.");
      return;
    }
    if (!addInventoryForm.uomId) {
      toast.error("Please select a UOM.");
      return;
    }
    const selected = availableProducts.find((p) => String(p.id) === String(addInventoryForm.productId));
    const uomId = addInventoryForm.uomId || selected?.uomId || "";
    const unitPriceNum = Number(addInventoryForm.unitPrice);
    const minQtyNum = Number(addInventoryForm.minOrderQuantity || 1);
    const payload = {
      supplierId: Number(supplierId),
      productId: Number(addInventoryForm.productId),
      uomId: Number(uomId),
      unitPrice: isNaN(unitPriceNum) ? 0 : unitPriceNum,
      minOrderQuantity: isNaN(minQtyNum) ? 1 : minQtyNum,
      leadTime: addInventoryForm.leadTime || "Not provided",
      status: addInventoryForm.status || "ACTIVE",
    };
    setAddInventoryLoading(true);
    try {
      await adminServices.postData("SupplierInventory", payload);
      toast.success("Supplier inventory added successfully.");
      setIsAddInventoryModalOpen(false);
      await refreshSupplierData();
    } catch (error) {
      console.error("Error adding supplier inventory:", error);
      toast.error(error.response?.data?.message || "Failed to add supplier inventory.");
    } finally {
      setAddInventoryLoading(false);
    }
  };

  // Import supplier inventory via POST /api/SupplierInventory/import/{supplierId}
  const handleImportInventory = async (data) => {
    const supplierId = supplierData?.originalData?.id ?? supplierData?.id ?? supplierData?.supplierId;
    if (!supplierId) {
      toast.error("Supplier not found. Cannot import inventory.");
      return;
    }
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL || ''}SupplierInventory/import/${supplierId}`,
        formData,
        {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('appToken'),
          },
        }
      );
      if (response?.data?.success) {
        const { successfulRecords, totalRecords, failedRecords } = response.data;
        let msg = response.data.message || "Supplier inventory imported successfully!";
        if (totalRecords !== undefined) {
          msg += ` (${successfulRecords || 0}/${totalRecords} records)`;
          if (failedRecords > 0) msg += `, ${failedRecords} failed`;
        }
        toast.success(msg);
      } else {
        toast.success("Supplier inventory imported successfully!");
      }
      setIsImportModalOpen(false);
      await refreshSupplierData();
    } catch (error) {
      console.error("Error importing supplier inventory:", error);
      toast.error(error.response?.data?.message || "Failed to import supplier inventory. Please try again.");
    } finally {
      setImportLoading(false);
    }
  };

  // Map API SupplierInventory response to ProductDetailModal format
  const mapSupplierInventoryToProduct = (data) => {
    if (!data) return null;
    const product = data.product || {};
    const sku = product.sku || product.productSku || data.sku || "-";
    const name = product.description || product.productName || product.name || data.description || "-";
    const price = data.unitPrice ?? product.marketPrice ?? product.unitPrice ?? 0;
    const priceStr = price !== undefined && price !== null ? `₱${Number(price).toLocaleString()}` : "-";
    const uomObj = data.uom || product.uom;
    const uom = (typeof uomObj === "object" && uomObj?.name) ? uomObj.name : (product.smallestUOM || product.uom || data.uom || "-");
    const moq = data.minOrderQuantity ?? data.moq ?? product.moq ?? 0;
    const leadTime = data.leadTime ?? product.leadTime ?? "-";
    const history = data.supplierInventoryPriceHistories || data.priceHistory || product.priceHistory || [];
    const formatTimestamp = (val) => {
      if (!val) return "-";
      try {
        const d = new Date(val);
        return isNaN(d.getTime()) ? val : d.toLocaleString();
      } catch {
        return val;
      }
    };
    const formatPrice = (val) =>
      val !== undefined && val !== null ? `₱${Number(val).toLocaleString()}` : "-";
    const priceHistory = Array.isArray(history) ? history.map((h) => {
      const user = h.editedByUser || h.editedBy || h.user || h.modifiedBy;
      const userName = typeof user === "object" && user
        ? [user.firstName, user.lastName].filter(Boolean).join(" ") || user.name || "-"
        : "-";
      const userEmail = typeof user === "object" && user ? (user.email || "-") : "-";
      return {
        id: h.id,
        timestamp: formatTimestamp(h.regDate || h.timestamp || h.date || h.createdAt),
        priceBefore: formatPrice(h.priceBefore ?? h.oldPrice),
        priceAfter: formatPrice(h.priceAfter ?? h.newPrice ?? h.unitPrice),
        editedBy: { name: userName, email: userEmail, avatar: typeof user === "object" && user?.avatar ? user.avatar : undefined }
      };
    }) : [];
    return {
      id: data.id ?? data.inventoryId ?? product.id,
      sku,
      itemName: name,
      unitPrice: priceStr,
      pricePerLoad: priceStr,
      uom,
      moq,
      leadTime: typeof leadTime === "number" ? `${leadTime} days` : (leadTime || "-"),
      image: product.image || product.imageUrl || data.image || null,
      priceHistory,
      originalData: data
    };
  };

  const handleSaveProduct = async (editedValues) => {
    const original = selectedProduct?.originalData;
    if (!original) {
      toast.error("Cannot save: product data not found.");
      throw new Error("No original data");
    }
    const inventoryId = original.inventoryId ?? original.id;
    if (!inventoryId) {
      toast.error("Cannot save: inventory ID not found.");
      throw new Error("No inventory ID");
    }
    const parseUnitPrice = (val) => {
      if (val === "" || val == null || val === undefined) return null;
      const str = String(val).replace(/[₱,\s]/g, "");
      const num = parseFloat(str);
      return isNaN(num) ? null : num;
    };
    const selectedUomId = editedValues.uomId ?? original.uomId ?? original.uom?.id;
    const selectedUomName = editedValues.uom ?? original.uom?.name ?? original.uom ?? "";
    const payload = {
      inventoryId: original.inventoryId ?? original.id,
      supplierId: original.supplierId,
      productId: original.productId ?? original.product?.id,
      uomId: selectedUomId,
      unitPrice: parseUnitPrice(editedValues.unitPrice) ?? original.unitPrice ?? 0,
      minOrderQuantity: editedValues.moq !== "" && editedValues.moq != null
        ? Number(editedValues.moq)
        : null,
      leadTime: editedValues.leadTime != null && String(editedValues.leadTime).trim() !== ""
        ? String(editedValues.leadTime).trim()
        : original.leadTime ?? null,
      status: original.status ?? "ACTIVE",
      supplier: (() => {
        const s = original.supplier ?? { id: original.supplierId };
        const t = s.type ?? { id: s.typeId ?? 0 };
        return { ...s, type: { ...t, name: t.name ?? "" } };
      })(),
      product: (() => {
        const p = original.product ?? { id: original.productId ?? original.product?.id };
        const sc = p.subclassfication ?? { id: p.subclassficationId ?? 0 };
        const pc = sc.productCategory ?? { id: sc.productCategoryId ?? 0 };
        return { ...p, subclassfication: { ...sc, name: sc.name ?? "", productCategory: { ...pc, category: pc.category ?? "" } } };
      })(),
      uom: selectedUomId
        ? { id: selectedUomId, name: selectedUomName }
        : (original.uom ?? { id: original.uomId ?? original.uom?.id })
    };
    try {
      await adminServices.updateData(`SupplierInventory/${inventoryId}`, payload);
      const res = await adminServices.getData(`SupplierInventory/${inventoryId}`);
      const data = res?.data;
      const mapped = data ? mapSupplierInventoryToProduct(data) : null;
      if (mapped) setSelectedProduct(mapped);
      toast.success("Product updated successfully.");
      await refreshSupplierData();
    } catch (error) {
      console.error("Error updating supplier inventory:", error);
      toast.error(error.response?.data?.message || "Failed to update product.");
      throw error;
    }
  };

  const handleRowClick = async (row) => {
    const inventoryId = row.originalData?.inventoryId ?? row.id;
    if (!inventoryId) {
      toast.error("Inventory ID not found.");
      return;
    }
    setProductDetailLoading(true);
    setIsProductModalOpen(true);
    setSelectedProduct(null);
    try {
      const response = await adminServices.getData(`SupplierInventory/${inventoryId}`);
      const data = response?.data;
      const mapped = mapSupplierInventoryToProduct(data);
      if (mapped) {
        setSelectedProduct(mapped);
      } else {
        toast.error("Could not load product details.");
        setIsProductModalOpen(false);
      }
    } catch (error) {
      console.error("Error fetching supplier inventory:", error);
      toast.error(error.response?.data?.message || "Failed to load product details.");
      setIsProductModalOpen(false);
    } finally {
      setProductDetailLoading(false);
    }
  };

  const handleSaveSupplier = async (updatedData) => {
    toast.success("Supplier updated successfully!");
    // Update local state immediately for better UX
    setSupplierData(prev => ({
      ...prev,
      supplierName: updatedData.supplierName || prev.supplierName,
      contactPerson: updatedData.contactPerson || updatedData.contactName || prev.contactPerson,
      phone: updatedData.phone || updatedData.contactNo || prev.phone,
      email: updatedData.email || prev.email,
      paymentMethod: updatedData.paymentMethod || prev.paymentMethod,
      shipmentPeriod: updatedData.shipmentPeriod || prev.shipmentPeriod,
      address: updatedData.address || prev.address,
      status: updatedData.status || prev.status
    }));

    // Optionally refresh supplier data from API
    if (supplier && (supplier.originalData?.id || supplier.id || supplier.supplierId)) {
      try {
        const supplierId = supplier.originalData?.id || supplier.id || supplier.supplierId;
        const response = await adminServices.getData(`Suppliers/${supplierId}`);
        if (response && response.data) {
          // Map API response to display format
          const updatedSupplier = {
            id: response.data.id,
            supplierId: response.data.id,
            supplierName: response.data.supplierName,
            name: response.data.supplierName,
            status: response.data.status,
            contactPerson: response.data.contactName,
            phone: response.data.contactNo,
            email: response.data.email,
            paymentMethod: response.data.paymentMethod,
            address: response.data.address,
            shipmentPeriod: response.data.shipmentPeriod,
            categories: supplierData.categories || [],
            businessCategory: supplierData.businessCategory || "",
            fulfillmentRate: supplierData.fulfillmentRate || "",
            standardShipmentPeriod: supplierData.standardShipmentPeriod || "",
            originalData: response.data
          };
          setSupplierData(updatedSupplier);
        }
      } catch (error) {
        console.error("Error refreshing supplier data:", error);
        // Keep the local update even if refresh fails
      }
    }
  };

  const productColumns = [
    {
      name: "SKU",
      selector: row => row.sku,
      sortable: true,
      width: "12%"
    },
    {
      name: "Item Name",
      selector: row => row.itemName,
      sortable: true,
      width: "28%"
    },
    {
      name: "Unit Price",
      selector: row => row.unitPrice,
      sortable: true,
      width: "14%"
    },
    {
      name: "UOM",
      selector: row => row.uom,
      sortable: true,
      width: "10%"
    },
    {
      name: "MOQ",
      selector: row => row.moq,
      sortable: true,
      width: "10%"
    },
    {
      name: "Lead Time",
      selector: row => row.leadTime,
      sortable: true,
      width: "14%"
    },
    {
      name: "",
      cell: (row) => (
        <button
          className="supplier-row-edit-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%"
          }}
          aria-label="Edit"
          type="button"
        >
          <Edit size={16} color="#111827" />
        </button>
      ),
      width: "6%",
      center: true
    },
  ];

  const customStyles = {
    table: {
      style: {
        tableLayout: 'fixed'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#0F2A40",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "center"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px"
      }
    },
    pagination: {
      style: {
        justifyContent: "center",
        flexWrap: "wrap"
      }
    }
  };

  if (!supplierData) {
    return (
      <div className="dashboard-content">
        <div className="supplier-detail-page" style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
          <button className="supplier-back-btn" onClick={onBack} style={{ marginBottom: 16 }}>
            <ArrowLeft size={20} />
          </button>
          <p>No supplier selected.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <LoadingModal show={importLoading} message="Importing supplier inventory..." />
      <div className="supplier-detail-page">
        {/* Header Section */}
        <div className="supplier-detail-header">
        <button className="supplier-back-btn" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <div className="supplier-detail-header-content">
          <StatusBadge status={supplierData.status} />
          <h1 className="dashboard-title">
            {supplierData.supplierName || supplierData.name} ({supplierData.supplierCode || supplierData.supplierId || supplierData.id})
          </h1>
          <div className="supplier-detail-spacer"></div>
          <button 
            className="supplier-edit-btn"
            onClick={() => setIsEditModalOpen(true)}
          >
            <Edit size={20} />
          </button>
        </div>
      </div>

      {/* Supplier Details Section */}
      <div className="supplier-details-section">
        <div className="supplier-details-grid">
          {/* Left Column */}
          <div className="supplier-details-column">
            <div className="supplier-detail-item">
              <User size={18} className="supplier-detail-icon" />
              <span className="supplier-detail-label">Contact Person:</span>
              <span className="supplier-detail-value">{supplierData.contactPerson}</span>
            </div>
            <div className="supplier-detail-item">
              <Phone size={18} className="supplier-detail-icon" />
              <span className="supplier-detail-label">Phone:</span>
              <span className="supplier-detail-value">{supplierData.phone}</span>
            </div>
            <div className="supplier-detail-item">
              <Truck size={18} className="supplier-detail-icon" />
              <span className="supplier-detail-label">Shipment Period:</span>
              <span className="supplier-detail-value">{supplierData.shipmentPeriod}</span>
            </div>
          </div>

          {/* Right Column */}
          <div className="supplier-details-column">
            <div className="supplier-detail-item">
              <Mail size={18} className="supplier-detail-icon" />
              <span className="supplier-detail-label">Email:</span>
              <span className="supplier-detail-value">{supplierData.email}</span>
            </div>
            <div className="supplier-detail-item">
              <CreditCard size={18} className="supplier-detail-icon" />
              <span className="supplier-detail-label">Payment Method:</span>
              <span className="supplier-detail-value">{supplierData.paymentMethod}</span>
            </div>
            <div className="supplier-detail-item">
              <MapPin size={18} className="supplier-detail-icon" />
              <span className="supplier-detail-label">Address:</span>
              <span className="supplier-detail-value">{supplierData.address}</span>
            </div>
          </div>
        </div>

        <div style={{ height: 1, backgroundColor: "#e5e7eb", margin: "20px 0" }} />

        <div className="supplier-details-grid">
          <div className="supplier-details-column">
            <div className="supplier-detail-item">
              <Package size={18} className="supplier-detail-icon" />
              <span className="supplier-detail-label">Category</span>
              <div className="supplier-detail-badges">
                {(supplierData.categories?.length ? supplierData.categories : []).map((category, index) => (
                  <span key={`${category}-${index}`} className="supplier-detail-category-badge">
                    {category}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="supplier-details-column">
            <div className="supplier-detail-item">
              <Package size={18} className="supplier-detail-icon" />
              <span className="supplier-detail-label">Supplier Type</span>
              <div className="supplier-detail-badges">
                {(supplierData.supplierTypeOptions?.length
                  ? supplierData.supplierTypeOptions
                  : (supplierData.supplierType ? [supplierData.supplierType] : [])
                ).map((t, index) => (
                  <span key={`${t}-${index}`} className="supplier-detail-category-badge">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Cards (restore) */}
      <div className="supplier-performance-cards">
        <div className="supplier-performance-card">
          <div className="supplier-performance-content">
            <div className="supplier-performance-info">
              <span className="supplier-performance-label">Fulfillment Rate</span>
              <span className="supplier-performance-value">{supplierData.fulfillmentRate}</span>
            </div>
            <Star size={24} className="supplier-performance-icon" />
          </div>
        </div>
        <div className="supplier-performance-card">
          <div className="supplier-performance-content">
            <div className="supplier-performance-info">
              <span className="supplier-performance-label">Standard Shipment Period</span>
              <span className="supplier-performance-value">{supplierData.shipmentPeriod}</span>
            </div>
            <Truck size={24} className="supplier-performance-icon" />
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="supplier-products-section">
        <div className="supplier-products-header">
          <h2 className="supplier-products-title">Products</h2>
          <div className="supplier-products-actions">
            <button
              className="supplier-import-btn"
              onClick={openAddInventoryModal}
              style={{
                backgroundColor: "#ffffff",
                color: "#000000",
                borderColor: "#000000",
                borderWidth: 1,
                borderStyle: "solid"
              }}
            >
              + Add Products
            </button>
            <button
              className="item-master-template-btn"
              onClick={handleDownloadTemplate}
            >
              <Download size={18} />
              Download Template
            </button>
            <button
              className="item-master-import-btn"
              onClick={() => setIsImportModalOpen(true)}
            >
              <Upload size={18} />
              Import CSV
            </button>
            <button
              className="item-master-export-btn"
              onClick={() => exportToCSV(products)}
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>
        <div className="supplier-products-table-section">
          <div className="supplier-products-table-container">
            <DataTable
              columns={productColumns}
              data={products}
              customStyles={customStyles}
              striped
              highlightOnHover
              noHeader={false}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[]}
              paginationComponent={CustomPagination}
              paginationComponentOptions={{
                rowsPerPageText: '',
                rangeSeparatorText: 'of',
                noRowsPerPage: true,
                selectAllRowsItem: false
              }}
              progressPending={productsLoading}
              noDataComponent={
                <div style={{ padding: "40px 20px", textAlign: "center", color: "#666", fontSize: "1rem" }}>
                  No products found
                </div>
              }
              onRowClicked={handleRowClick}
              pointerOnHover
            />
          </div>
        </div>
      </div>

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        loading={productDetailLoading}
        onSave={handleSaveProduct}
      />

      {/* Supplier Edit Modal */}
      <SupplierEditModal
        supplier={supplierData}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveSupplier}
      />

      {/* Import Supplier Inventory Modal */}
      <ImportSupplierInventoryModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportInventory}
      />

      {/* Add Supplier Inventory Modal */}
      <Modal
        isOpen={isAddInventoryModalOpen}
        onRequestClose={() => setIsAddInventoryModalOpen(false)}
        className="import-products-modal"
        overlayClassName="import-products-modal-overlay"
        ariaHideApp={false}
      >
        <div className="import-products-modal-content">
          <button className="import-products-close-btn-top" onClick={() => setIsAddInventoryModalOpen(false)}>
            <X size={20} />
          </button>
          <div className="import-products-body">
            <h3 className="import-products-card-title">Add Supplier Inventory</h3>
            <div className="import-products-form-card">
              <div className="import-products-field">
                <label
                  className="import-products-label"
                  style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}
                >
                  Product
                </label>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setProductDropdownOpen((prev) => !prev)}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14,
                      backgroundColor: "#ffffff",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    {(() => {
                      if (!addInventoryForm.productId) return "Select product";
                      const selected = availableProducts.find(
                        (p) => String(p.id) === String(addInventoryForm.productId)
                      );
                      return selected ? `${selected.sku} - ${selected.name}` : "Select product";
                    })()}
                  </button>
                  {productDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: 44,
                        left: 0,
                        right: 0,
                        backgroundColor: "#ffffff",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                        zIndex: 10,
                        padding: 8,
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Search by SKU or name"
                        value={productDropdownSearch}
                        onChange={(e) => setProductDropdownSearch(e.target.value)}
                        style={{
                          width: "100%",
                          height: 32,
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          padding: "0 10px",
                          fontSize: 13,
                          marginBottom: 8,
                        }}
                      />
                      <div
                        style={{
                          maxHeight: 220,
                          overflowY: "auto",
                        }}
                      >
                        {(Array.isArray(availableProducts) ? availableProducts : [])
                          .filter((p) => {
                            const q = productDropdownSearch.trim().toLowerCase();
                            if (!q) return true;
                            const sku = (p.sku || "").toLowerCase();
                            const name = (p.name || "").toLowerCase();
                            return sku.includes(q) || name.includes(q);
                          })
                          .map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                const productId = p.id;

                                let productUoms = [];
                                if (Array.isArray(p.productUoms) && p.productUoms.length > 0) {
                                  productUoms = p.productUoms.map(normalizeUom).filter(Boolean);
                                }
                                if (productUoms.length === 0 && p.assignedUomIds && p.assignedUomIds.length > 0 && uomList.length > 0) {
                                  productUoms = p.assignedUomIds
                                    .map((id) => uomList.find((u) => String(u.id) === String(id)))
                                    .filter(Boolean);
                                }
                                if (productUoms.length === 0 && uomList.length > 0) {
                                  const names = Array.isArray(p.assignedUomNames) ? p.assignedUomNames : [];
                                  productUoms = names
                                    .map((name) =>
                                      uomList.find(
                                        (u) => String(u.name).toLowerCase() === String(name).toLowerCase()
                                      )
                                    )
                                    .filter(Boolean);
                                }
                                const seen = new Set();
                                productUoms = productUoms.filter((u) => {
                                  if (!u || u.id == null || seen.has(String(u.id))) return false;
                                  seen.add(String(u.id));
                                  return true;
                                });

                                const defaultUomId = productUoms[0]?.id || p.uomId || "";
                                // eslint-disable-next-line no-console
                                console.log("[SupplierDetail] selected product:", p, "productUoms:", productUoms, "uomList:", uomList);
                                setSelectedProductUoms(productUoms);

                                setAddInventoryForm((prev) => ({
                                  ...prev,
                                  productId,
                                  uomId: defaultUomId,
                                  unitPrice:
                                    prev.unitPrice !== "" && prev.unitPrice != null
                                      ? prev.unitPrice
                                      : p.marketPrice ?? "",
                                }));
                                setProductDropdownOpen(false);
                              }}
                              style={{
                                width: "100%",
                                padding: "6px 8px",
                                textAlign: "left",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                fontSize: 13,
                              }}
                            >
                              <div style={{ fontWeight: 500 }}>{p.sku}</div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>{p.name}</div>
                            </button>
                          ))}
                        {!availableProductsLoading &&
                          (!availableProducts || availableProducts.length === 0) && (
                            <div
                              style={{
                                padding: "8px 4px",
                                fontSize: 12,
                                color: "#6b7280",
                              }}
                            >
                              No products found
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="import-products-field">
                <label
                  className="import-products-label"
                  style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}
                >
                  Unit of Measure (UOM)
                </label>
                <select
                  className="import-products-select"
                  value={addInventoryForm.uomId}
                  onChange={(e) =>
                    setAddInventoryForm((prev) => ({ ...prev, uomId: e.target.value }))
                  }
                  disabled={!addInventoryForm.productId || uomLoading}
                  style={{
                    width: "100%",
                    height: 40,
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    padding: "0 12px",
                    fontSize: 14,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <option value="">
                    {!addInventoryForm.productId
                      ? "Select a product first"
                      : selectedProductUoms.length === 0
                      ? "No UOMs assigned"
                      : "Select UOM"}
                  </option>
                  {selectedProductUoms.map((uom) => (
                    <option key={uom.id} value={uom.id}>
                      {uom.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="import-products-field">
                <label
                  className="import-products-label"
                  style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}
                >
                  Unit Price
                </label>
                <input
                  type="number"
                  className="import-products-input"
                  value={addInventoryForm.unitPrice}
                  onChange={(e) =>
                    setAddInventoryForm((prev) => ({ ...prev, unitPrice: e.target.value }))
                  }
                  placeholder="Enter unit price"
                  style={{
                    width: "100%",
                    height: 40,
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    padding: "0 12px",
                    fontSize: 14,
                  }}
                />
              </div>
              <div className="import-products-field">
                <label
                  className="import-products-label"
                  style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}
                >
                  Minimum Order Quantity
                </label>
                <input
                  type="number"
                  className="import-products-input"
                  value={addInventoryForm.minOrderQuantity}
                  onChange={(e) =>
                    setAddInventoryForm((prev) => ({
                      ...prev,
                      minOrderQuantity: e.target.value,
                    }))
                  }
                  placeholder="Enter MOQ"
                  style={{
                    width: "100%",
                    height: 40,
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    padding: "0 12px",
                    fontSize: 14,
                  }}
                />
              </div>
              <div className="import-products-field">
                <label
                  className="import-products-label"
                  style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}
                >
                  Lead Time
                </label>
                <input
                  type="text"
                  className="import-products-input"
                  value={addInventoryForm.leadTime}
                  onChange={(e) =>
                    setAddInventoryForm((prev) => ({ ...prev, leadTime: e.target.value }))
                  }
                  placeholder="e.g. 5 days or Not provided"
                  style={{
                    width: "100%",
                    height: 40,
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    padding: "0 12px",
                    fontSize: 14,
                  }}
                />
              </div>
              <div className="import-products-field">
                <label
                  className="import-products-label"
                  style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}
                >
                  Status
                </label>
                <select
                  className="import-products-select"
                  value={addInventoryForm.status}
                  onChange={(e) =>
                    setAddInventoryForm((prev) => ({ ...prev, status: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    height: 40,
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    padding: "0 12px",
                    fontSize: 14,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
              <div className="import-products-card-actions">
                <button
                  className="import-products-cancel-btn"
                  onClick={() => setIsAddInventoryModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="import-products-import-btn"
                  onClick={handleAddSupplierInventory}
                  disabled={addInventoryLoading}
                >
                  {addInventoryLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
    </div>
  );
};

export default SupplierDetail;
